/* eslint-disable sort-keys */
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { FC, useEffect, useState } from 'react';
import StatusCards from 'src/components/StatusCards/StatusCards';
import SearchAcopsFields from './components/SearchAcopsFields';
import AcopResultTable from './components/AcopResultTable';
import { useGetStatusCount, StatusCountResponse } from '../hooks/statusCount';
import { useTheme } from '@mui/system';
import camelCase from 'lodash/camelCase';
import { useGetAcopList } from '../hooks/acopList';
import { useDownloadAcopList } from '../hooks/downloadAcopList';
import { DeleteAcopResponse, useDeleteAcop } from '../hooks/deleteAcops';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import AcopMaintenanceButtons from './components/AcopMaintenanceButtons';
import DelinkResponsePopUp from './components/DelinkResponsePopup';
import { useSearchParams } from 'react-router-dom';

const AcopMaintenance: FC = () => {
  const translate = useTranslate();
  const theme = useTheme();

  const [searchQuery, setSearchQuery] = useState<{
    AcopStatus?: string;
    AcopTracking?: string;
    BCN?: string;
    BidNumber?: string;
    BundleId?: string;
    IsOpenOrdersOnly?: boolean;
    OrderEndDate?: string;
    OrderNumber?: string;
    OrderStartDate?: string;
    Program?: number;
    Vendor?: number;
  }>({});
  const [pageSetUp, setPageSetUp] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isBundleMode, setIsBundleMode] = useState(false);
  const [deleteAcopResponse, setDeleteAcopResponse] = useState<
    DeleteAcopResponse | undefined
  >(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const dmR_Id = searchParams.get('dmrId');
  const [dmrAction, setDmrAction] = useState('');

  const partialReqBody = {
    ...(Boolean(searchQuery.AcopStatus) && {
      AcopStatus: searchQuery.AcopStatus,
    }),
    ...(Boolean(searchQuery.AcopTracking) && {
      AcopTrackingNumber: Number(searchQuery.AcopTracking),
    }),
    ...(Boolean(searchQuery.BCN) && {
      Bcn: searchQuery.BCN,
    }),
    ...(Boolean(searchQuery.BidNumber) && {
      BidNumber: searchQuery.BidNumber,
    }),
    ...(Boolean(searchQuery.BundleId) && {
      BundleId: searchQuery.BundleId,
    }),
    ...(Boolean(searchQuery.OrderEndDate) && {
      OrderEndDate: searchQuery.OrderEndDate,
    }),
    ...(Boolean(searchQuery.OrderNumber) && {
      OrderNumber: searchQuery.OrderNumber,
    }),
    ...(Boolean(searchQuery.OrderStartDate) && {
      OrderStartDate: searchQuery.OrderStartDate,
    }),
    ...(Boolean(searchQuery.Program) && {
      ProgramId: searchQuery.Program,
    }),
    ...(Boolean(searchQuery.Vendor) && {
      VendorId: searchQuery.Vendor,
    }),
    ...(Boolean(searchQuery.IsOpenOrdersOnly) && {
      IsOpenOrdersOnly: searchQuery.IsOpenOrdersOnly,
    }),
    ...(Boolean(searchParams.get('dmrId')) && {
      DmrId: dmR_Id,
    }),
  };

  const { data: statusCount, isLoading: isStatusLoading } = useGetStatusCount({
    ...partialReqBody,
  });
  const { data: acopListResponse, isLoading: isAcopListLoading } =
    useGetAcopList({
      ...partialReqBody,
      PageNo: pageSetUp.page,
      PageSize: pageSetUp.pageSize,
    });
  const { mutateAsync } = useDownloadAcopList({
    ...partialReqBody,
    TotalCount: acopListResponse?.responsePreamble.totalCount ?? 0,
  });
  const { isLoading: isDeleteLoading, mutateAsync: deleteAcop } = useDeleteAcop(
    acopListResponse?.data.filter((item) => selectedRows.includes(item.id)) ??
      [],
    searchQuery.BundleId
  );

  const statuses = [
    { key: 'TotalStatuses', color: theme.palette.text.secondary },
    {
      key: 'ActiveStatuses',
      color: theme.palette.success.main,
      suffix: '(OL)',
    },
    { key: 'ExpiredStatuses', color: theme.palette.error[600] },
    {
      key: 'PendingStatuses',
      color: theme.palette.warning[600],
      suffix: '(AL)',
    },
  ];

  const statusList = statuses.map(({ color, key, suffix }) => {
    const translatedName = translate(`AcopMaintenance.${key}Name`);
    const statusNameParts = [translatedName];

    if (suffix) {
      statusNameParts.push(suffix);
    }

    return {
      id: key,
      countColor: color,
      count: statusCount?.[camelCase(key) as keyof StatusCountResponse] ?? 0,
      statusName: statusNameParts.join(' '),
    };
  });

  const sx = {
    background: '#FFFFFF',
    border: '1px solid #CCCCCC',
    borderRadius: '16px',
    boxShadow: '0px 0px 9px 2px #00000021',
    padding: 2,
  };

  const handleAcopDelete = (dmR_Action: string, reason: string) => {
    setDmrAction(
      dmR_Action === 'DELINK_RECOST' ? 'Delink & Recost' : dmR_Action
    );
    deleteAcop({
      dmR_Action,
      reason,
    })
      .then((response) => {
        setSelectedRows([]);
        setDeleteAcopResponse(response);
        searchParams.set('dmrId', response.dmR_Id);
        setSearchParams(searchParams, { replace: true });
      })
      .catch(() => {
        setDeleteAcopResponse({
          message: translate('AcopMaintenance.AcopDeleteError'),
        } as DeleteAcopResponse);
      });
  };

  useEffect(() => {
    setIsBundleMode(Boolean(searchQuery.BundleId));
  }, [searchQuery.BundleId]);

  return (
    <Box p={2} position={'relative'}>
      {isDeleteLoading && (
        <div style={{ position: 'absolute', zIndex: 10 }}>
          <CenterLoader overlay />
        </div>
      )}
      <Stack gap={3}>
        <Stack>
          <Typography sx={{ fontSize: '22px', fontWeight: 500 }}>
            {translate('AcopMaintenance.PageTitle')}
          </Typography>
          <Typography sx={{ fontSize: '14px' }}>
            {translate('AcopMaintenance.PageSubTitle')}
          </Typography>
        </Stack>
        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          gap={2}
          sx={{ '& > div': { flex: 1 } }}
        >
          {statusList.map((status) => (
            <StatusCards
              count={status.count}
              countColor={status.countColor}
              isLoading={isStatusLoading}
              key={status.id}
              statusName={status.statusName}
            />
          ))}
        </Stack>
        <Box sx={sx}>
          <Stack gap={2}>
            <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
              {translate('AcopMaintenance.SearchFieldsTitle')}
            </Typography>
            <SearchAcopsFields
              searchQuery={searchQuery}
              setPageSetUp={setPageSetUp}
              setSearchQuery={setSearchQuery}
            />
          </Stack>
        </Box>
        <Box sx={sx}>
          <Stack gap={2}>
            <AcopMaintenanceButtons
              downloadMutateAsync={mutateAsync}
              handleAcopDelete={handleAcopDelete}
              isDelinkDisabled={
                !!dmR_Id ||
                (isBundleMode && acopListResponse?.data.length !== 0
                  ? false
                  : selectedRows.length === 0)
              }
              isDownloadDisabled={
                !!dmR_Id ||
                (isBundleMode && acopListResponse?.data.length === 0)
              }
            />
            <AcopResultTable
              acopListData={acopListResponse}
              bundleMode={isBundleMode}
              isAcopListLoading={isAcopListLoading}
              isOpenOrdersOnly={searchQuery.IsOpenOrdersOnly}
              pageSetUp={pageSetUp}
              selectedRows={selectedRows}
              setPageSetUp={setPageSetUp}
              setSelectedRows={setSelectedRows}
            />
          </Stack>
        </Box>
      </Stack>
      {deleteAcopResponse && (
        <DelinkResponsePopUp
          dmr_Action={dmrAction}
          handleClose={() => setDeleteAcopResponse(undefined)}
          responseData={deleteAcopResponse}
        />
      )}
    </Box>
  );
};

export default AcopMaintenance;
